import { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { esitoUpload } from "@/lib/upload";
import { X, Loader2, Upload, FileText, Plus } from "lucide-react";
import AnagraficaFieldsGroups, { requisitiCliente } from "@/components/AnagraficaFieldsGroups";
import ReferenteSelect from "@/components/ReferenteSelect";
import { ImportDocsButtons } from "@/components/ImportDocsButtons";
import DriveExtractButton from "@/components/DriveExtractButton";

export const EXT_FIELDS = [
  ["codice_fiscale", "Codice fiscale"],
  ["data_nascita", "Data di nascita"],
  ["luogo_nascita", "Luogo di nascita"],
  ["nazionalita", "Nazionalità"],
  ["indirizzo", "Residenza — Via e n. civico"],
  ["residenza_citta", "Residenza — Città / Prov. / CAP"],
  ["numero_passaporto", "Numero passaporto"],
  ["data_rilascio", "Data rilascio doc."],
  ["luogo_rilascio", "Luogo rilascio"],
  ["data_scadenza", "Scadenza passaporto"],
  ["numero_documento", "Numero documento"],
  ["rilascio_documento", "Data rilascio documento"],
  ["scadenza_documento", "Scadenza documento"],
  ["numero_permesso_soggiorno", "N. permesso soggiorno"],
  ["scadenza_permesso", "Scadenza permesso"],
  ["residenza_tipo", "Residenza"],
  ["residenza_provincia", "Residenza — Provincia"],
  ["residenza_cap", "Residenza — CAP"],
  ["residenza_stato", "Stato di residenza"],
  ["luogo_rilascio_permesso", "Permesso — Luogo di rilascio"],
  ["data_emissione_permesso", "Permesso — Data emissione"],
  ["luogo_rilascio_documento", "Documento — Luogo di rilascio"],
];

export default function AnagraficaDialog({ cliente, onClose, onSaved }) {
  const zonaFileRef = useRef(null);
  const ultimoDropAna = useRef(0);
  const base = { nome: "", cognome: "", email: "", telefono: "", note: "", pec: "", rappresentante_legale: "" };
  const init = { ...base };
  [...Object.keys(base), ...EXT_FIELDS.map(([k]) => k)].forEach((k) => { init[k] = cliente?.[k] || ""; });
  init.referente_id = cliente?.referente_id || "";
  init.is_azienda = cliente?.is_azienda || false;
  const [form, setForm] = useState(init);
  const [extra, setExtra] = useState({ ...(cliente?.campi_extra || {}) });
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);

  const stripExt = (n) => (n || "").replace(/\.[^.]+$/, "");
  const uid = () => Math.random().toString(36).slice(2, 9);

  const setBuiltin = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setCustom = (k, v) => setExtra((p) => ({ ...p, [k]: v }));

  const LABELS = { nome: "Nome", cognome: "Cognome", luogo_nascita: "Luogo di nascita",
    data_nascita: "Data di nascita", nazionalita: "Nazionalità", residenza_tipo: "Residenza (Italia/Estero)",
    indirizzo: "Via e n. civico", residenza_citta: "Città", residenza_provincia: "Provincia",
    residenza_cap: "CAP", residenza_stato: "Stato di residenza" };

  const save = async () => {
    if (!form.is_azienda) {
      const mancanti = requisitiCliente((k) => form[k]).filter((k) => !String(form[k] || "").trim());
      if (mancanti.length) return toast.error(`Compila i dati obbligatori: ${mancanti.map((k) => LABELS[k] || k).join(", ")}`);
    }
    setSaving(true);
    try {
      await api.patch(`/clienti/${cliente.id}/anagrafica`, { ...form, campi_extra: extra });
      if (items.length > 0) {
        const fd = new FormData();
        items.forEach((it) => { fd.append("files", it.file); fd.append("names", it.name || stripExt(it.file.name)); });
        try {
          const { data: up } = await api.post(`/clienti/${cliente.id}/upload`, fd);
          toast.success(esitoUpload(up, "Anagrafica aggiornata"));
        } catch (e) {
          toast.warning(e?.response?.data?.detail || "Anagrafica aggiornata, ma upload su Drive non riuscito");
        }
      } else {
        toast.success("Anagrafica aggiornata");
      }
      setItems([]);
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="anagrafica-dialog">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col border-t-2 border-[#C9A84C] shadow-lg">
        <div className="flex items-center justify-between px-6 py-3 border-b border-[#1A2B4A]/10">
          <h3 className="font-serif text-2xl text-[#1A2B4A]">Anagrafica · {cliente.nome} {cliente.cognome}</h3>
          <button data-testid="anagrafica-close" onClick={onClose} className="text-[#4a5568] hover:text-[#1A2B4A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <ImportDocsButtons currentValues={{ ...form, ...extra }} onExtract={(f, enriched) => {
              setForm((prev) => ({ ...prev, ...f }));
              const cognome = (f.cognome || form.cognome || "").trim();
              const nome = (f.nome || form.nome || "").trim();
              const cli = [cognome, nome].filter(Boolean).join(" ");
              if (Object.keys(f || {}).length > 0 && enriched?.length) setItems((prev) => [...prev, ...enriched.map(({ file, tipo }) => {
                const base = (tipo || stripExt(file.name) || "Documento").trim();
                return { uid: uid(), file, name: cli ? `${base} (${cli})` : base };
              })]);
            }} />
            <div className="mt-2">
              <DriveExtractButton clienteId={cliente.id} currentValues={{ ...form, ...extra }}
                onExtract={(f) => setForm((prev) => ({ ...prev, ...f }))} />
            </div>
            <p className="text-[11px] text-[#4a5568] mt-1.5">I documenti importati vengono allegati e caricati nella cartella Drive del cliente al salvataggio; puoi correggere i dati estratti qui sotto.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#1A2B4A] cursor-pointer select-none" data-testid="ana-is-azienda-label">
            <input type="checkbox" data-testid="ana-is-azienda" checked={!!form.is_azienda}
              onChange={(e) => setBuiltin("is_azienda", e.target.checked)} className="accent-[#C9A84C] w-4 h-4" />
            È un'azienda / persona giuridica
          </label>
          {form.is_azienda ? (
            <div className="rounded-lg border border-[#1A2B4A]/15 bg-white p-4" data-testid="ana-azienda-fields">
              <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#1A2B4A] mb-3">Dati azienda</h4>
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
                {[["nome", "Ragione sociale"], ["codice_fiscale", "Partita IVA / Codice Fiscale"], ["indirizzo", "Sede legale (indirizzo)"], ["pec", "PEC"], ["email", "Email"], ["rappresentante_legale", "Rappresentante legale"], ["telefono", "Telefono"]].map(([k, label]) => (
                  <div key={k} className={k === "indirizzo" ? "sm:col-span-2" : ""}>
                    <label className="text-xs text-[#4a5568]">{label}</label>
                    <input data-testid={`ana-${k}`} value={form[k] || ""} onChange={(e) => setBuiltin(k, e.target.value)}
                      className="w-full mt-1 px-2.5 py-1.5 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <AnagraficaFieldsGroups
                clienteId={cliente?.id}
                baseRequired
                getValue={(f) => (f.builtin ? form[f.key] : extra[f.key])}
                setValue={(f, v) => (f.builtin ? setBuiltin(f.key, v) : setCustom(f.key, v))}
              />
            </>
          )}
          <ReferenteSelect value={form.referente_id} onChange={(id) => setBuiltin("referente_id", id ?? "")} testid="ana-referente" />

          <div>
            <label className="block text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Documenti (opzionale)</label>
            <p className="text-xs text-[#4a5568] mt-1 mb-2">Carica file o foto (carta d'identità, permesso di soggiorno…). Verranno salvati nella cartella Drive del cliente al salvataggio.</p>
            <div data-testid="ana-upload-zone" onDragEnter={(e) => e.preventDefault()} onDragOver={(e) => e.preventDefault()}
              onClick={(e) => { if (e.currentTarget.contains(e.target) && Date.now() - ultimoDropAna.current > 1500) zonaFileRef.current?.click(); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); ultimoDropAna.current = Date.now(); const dropped = Array.from(e.dataTransfer.files || []); if (dropped.length) setItems([...items, ...dropped.map((f) => ({ uid: uid(), file: f, name: stripExt(f.name) }))]); }}
              className="flex flex-col items-center justify-center gap-1 border border-dashed border-[#1A2B4A]/30 rounded-sm py-6 cursor-pointer hover:border-[#C9A84C] transition-colors">
              <Upload className="w-5 h-5 text-[#1A2B4A]" />
              <span className="text-xs text-[#4a5568]">Trascina qui i file o clicca per selezionare</span>
              <input ref={zonaFileRef} data-testid="ana-file-input" type="file" multiple accept="image/*,application/pdf,.doc,.docx"
                className="hidden" onChange={(e) => setItems([...items, ...Array.from(e.target.files).map((f) => ({ uid: uid(), file: f, name: stripExt(f.name) }))])} />
            </div>
            {items.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="ana-upload-items">
                {items.map((it, i) => (
                  <div key={it.uid} className="border border-[#1A2B4A]/10 rounded-sm p-2.5">
                    <div className="flex items-center gap-2 text-[11px] text-[#4a5568] mb-1.5">
                      <FileText className="w-3.5 h-3.5" /> <span className="truncate">{it.file.name}</span>
                      <button data-testid={`ana-doc-remove-${i}`} className="ml-auto" onClick={() => setItems(items.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5 text-[#9b2c2c]" /></button>
                    </div>
                    <div className="flex gap-2">
                      <input data-testid={`ana-doc-name-${i}`} value={it.name}
                        onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                        placeholder="es. Passaporto"
                        className="flex-1 px-3 py-1.5 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
                      <button data-testid={`ana-doc-addname-${i}`} title="Aggiungi nome cliente"
                        onClick={() => setItems(items.map((x, j) => j === i ? { ...x, name: (x.name || "") + ` (${[form.cognome, form.nome].filter(Boolean).join(" ")})` } : x))}
                        className="inline-flex items-center gap-1 text-xs border border-[#1A2B4A]/20 rounded-sm px-2 hover:border-[#C9A84C]">
                        <Plus className="w-3 h-3" /> cliente
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-3 border-t border-[#1A2B4A]/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
          <button data-testid="anagrafica-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}
